import { use } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import useCallPlanInternalNumbers from 'features/care-connect-settings/hooks/call-plan-internal-numbers';
import useInternalNumberValidation from 'features/care-connect-settings/hooks/internal-number-validation';
import styles from 'features/care-connect-settings/views/components/call-plan-internal-numbers/call-plan-internal-numbers.module.scss';

const CallPlanInternalNumbers = (): React.JSX.Element => {
  const { t } = useTranslation('careConnectSettings', { keyPrefix: 'callPlanInternalNumbers' });
  const { callPlanDetails } = use(CareConnectSettingsProviderContext);
  const { callPlanForm, isEditing, readCallPlanData } = callPlanDetails;
  const { internalNumbers, internalNumbersEmptyErrorMessage, canAddInternalNumber, addInternalNumber, removeInternalNumber } =
    useCallPlanInternalNumbers(callPlanForm);
  const { getValidationRules, handleDigitsOnlyChange, handleBeforeInput } = useInternalNumberValidation();

  const renderInternalNumberRow = (internalNumber: (typeof internalNumbers)[number], index: number): React.JSX.Element => (
    <div key={internalNumber.id} className={styles.internalNumberInput}>
      <Controller
        name={`internalNumbers.${index}.number`}
        control={callPlanForm.control}
        rules={getValidationRules(index, internalNumber.number)}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            variant="outlined"
            className={styles.internalNumberTextField}
            error={!!error}
            helperText={error?.message}
            value={field.value ?? ''}
            slotProps={{
              htmlInput: {
                maxLength: 5,
                inputMode: 'numeric',
                onBeforeInput: handleBeforeInput,
              },
            }}
            onChange={(event) => handleDigitsOnlyChange(event.target.value, field.onChange)}
          />
        )}
      />
      <IconButton className={styles.deleteButton} onClick={() => removeInternalNumber(index)}>
        <DeleteForeverOutlinedIcon />
      </IconButton>
    </div>
  );

  return (
    <div className={styles.container}>
      <Typography variant="h5">{t('title')}</Typography>
      {isEditing
        ? internalNumbers.map((internalNumber, index) => renderInternalNumberRow(internalNumber, index))
        : readCallPlanData?.internalNumbers.map((internalNumber) => (
            <div key={internalNumber.number} className={styles.viewingInternalNumberRow}>
              <Typography variant="subtitle1">{internalNumber.number}</Typography>
            </div>
          ))}
      {internalNumbersEmptyErrorMessage && (
        <Typography color="error" variant="body2">
          {internalNumbersEmptyErrorMessage}
        </Typography>
      )}
      {isEditing && canAddInternalNumber && (
        <Button variant="text" type="button" className={styles.addInternalNumberButton} onClick={addInternalNumber}>
          <AddCircleOutlineIcon sx={{ marginRight: 1 }} fontSize="small" />
          <Typography variant="body1" color="primary">
            {t('addInternalNumberButton')}
          </Typography>
        </Button>
      )}
    </div>
  );
};

export default CallPlanInternalNumbers;
